/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  orderBy,
  collection,
  doc,
  query,
  where,
  getDocs,
  serverTimestamp,
  runTransaction,
  increment,
} from "firebase/firestore";
import { toast } from "react-hot-toast";
import { db } from "../firebaseConfig";
import { useState, useEffect } from "react";
import { calculateHash } from "../utils/helpers";
import dayjs from "dayjs";
import { UserAuth } from "../context/authContext";
import { clsx } from "clsx";
import { FaRegClipboard } from "react-icons/fa";
import { FiSearch, FiCpu } from "react-icons/fi";
//import BlockTransactionsModal from "./BlockTransactionsModal";

export default function BlocksTable() {
  const [roomId, setId] = useState(localStorage.getItem("roomId") ?? "");
  const [selectedBlock, setSelectedBlock] = useState();
  const [selectedBlockForModal, setSelectedBlockForModal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openBlockModal = (block) => {
    setSelectedBlockForModal(block);
    setIsModalOpen(true);
  };

  const [blocks, setBlocks] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("hash");
  const { user } = UserAuth();

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const fetchRoomTransactions = async () => {
    if (!roomId) return;

    try {
      const roomRef = doc(db, "rooms", roomId);

      let tempBlocks = [];

      const blocksRes = await getDocs(
        query(collection(roomRef, "blocks"), orderBy("timestamp", "asc"))
      );

      tempBlocks = [
        ...tempBlocks,
        ...blocksRes.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })),
      ];

      setBlocks(tempBlocks);

      const transactionsRes = await getDocs(
        query(
          collection(roomRef, "transactions"),
          where("status", "==", "Added to block"),
          orderBy("addedAt", "asc")
        )
      );

      const transactions = transactionsRes.docs.reduce((result, value, index, array) => {
        if (index % 5 === 0)
          result.push({
            transactions: array.slice(index, index + 5).map((v) => ({ id: v.id, ...v.data() })),
          });
        return result;
      }, []);

      setPendingTransactions(transactions);
    } catch (error) {
      console.error("Error fetching room transactions:", error);
      toast.error("Error fetching room transactions.");
    }
  };

  async function saveBlock(calculatedHash, timestamp, previousBlockHash, transactions, nonce) {
    try {
      const reward = transactions.reduce((acc, curr) => {
        return acc + curr.amount * 0.1;
      }, 0);
      const receivers = Object.fromEntries(
        (
          await getDocs(
            query(
              collection(db, "users"),
              where(
                "publicKey",
                "in",
                transactions.map((t) => t.receiver)
              )
            )
          )
        ).docs.map((user) => [user.data().publicKey, { id: user.id, ...user.data() }])
      );

      await runTransaction(db, async (dbTransaction) => {
        const res = dbTransaction.set(doc(db, "rooms", roomId, "blocks", calculatedHash), {
          timestamp,
          previousBlockHash,
          hash: calculatedHash,
          transactions,
          nonce,
          index: blocks.length,
          created_at: serverTimestamp(),
        });

        // Reward for miner
        dbTransaction.update(doc(db, "users", user.email), {
          balance: increment(reward),
        });

        transactions.forEach((t) => {
          // Send trnasaction amount
          receivers.hasOwnProperty(t.receiver) &&
            dbTransaction.update(doc(db, "users", receivers[t.receiver].email), {
              balance: increment(t.amount * 0.9),
            });

          dbTransaction.update(doc(db, "rooms", roomId, "transactions", t.id), {
            status: "Added to chain",
          });
        });
      });

      toast.success("Added block to blockchain");
      fetchRoomTransactions();
    } catch (e) {
      console.error("Failed to save block", e);
      toast.error("Failed to save block");
    }
  }

  useEffect(() => {
    if (roomId) fetchRoomTransactions();
  }, [roomId]);

  const renderTrimmedAddress = (address) => `${address.substring(0, 10)}...`;

  const copyToClipboard = (e, data) => {
    e.preventDefault(); // Prevent the default action associated with the event

    navigator.clipboard.writeText(data).then(
      () => {
        toast.success("Copied Successfully");
      },
      (err) => {
        toast.error("Failed to copy");
      }
    );
  };

  return (
    <div className="max-w-[1600px] space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-3xl font-bold text-white tracking-tight">Blocks Explorer</h2>
          <p className="text-text-muted mt-1">
            Blockchain state for Room: <span className="text-primary font-mono">{roomId}</span>
          </p>
        </div>

        <div className="flex items-center gap-3 bg-surface/50 border border-white/5 rounded-2xl p-2 backdrop-blur-md">
          <div className="relative">
            <FiSearch
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
              size={16}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search blocks..."
              className="pl-10 pr-4 py-2 bg-background/50 border border-white/5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all w-64"
            />
          </div>
          <select
            value={filter}
            onChange={handleFilterChange}
            className="bg-background/50 border border-white/5 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="hash">Block Hash</option>
            <option value="nonce">Nonce</option>
            <option value="sender">Sender</option>
            <option value="receiver">Receiver</option>
          </select>
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/5 bg-surface/50">
                <th className="p-4 font-semibold uppercase tracking-wider text-[10px] text-text-muted">
                  Block Detail
                </th>
                <th className="p-4 font-semibold uppercase tracking-wider text-[10px] text-text-muted">
                  Transactions
                </th>
                <th className="p-4 font-semibold uppercase tracking-wider text-[10px] text-text-muted text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {[...blocks, ...pendingTransactions].map((block, index) => (
                <tr
                  key={index}
                  className={clsx(
                    "transition-colors group border-b border-white/5",
                    block.hash ? "hover:bg-white/5 cursor-pointer" : "hover:bg-white/2"
                  )}
                  onClick={() => block.hash && openBlockModal(block)}
                >
                  <td className="p-4 align-top max-w-[300px]">
                    {!block.hash ? (
                      <div className="flex flex-col gap-2">
                        <span className="text-yellow-500 font-bold flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                          UNMINED BLOCK
                        </span>
                        <p className="text-xs text-text-muted">
                          Waiting for a miner to process transactions.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] font-bold text-text-muted uppercase">
                            Hash
                          </span>
                          <div className="flex items-center gap-2 group/field">
                            <code className="text-xs text-primary font-mono truncate max-w-[200px]">
                              {block.hash}
                            </code>
                            <button
                              onClick={(e) => copyToClipboard(e, block.hash)}
                              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 rounded"
                            >
                              <FaRegClipboard size={12} className="text-text-muted" />
                            </button>
                          </div>
                        </div>
                        {block.timestamp && (
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-bold text-text-muted uppercase">
                              Timestamp
                            </span>
                            <span className="text-xs text-white">
                              {dayjs.unix(block.timestamp).format("MMM DD, YYYY HH:mm:ss")}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <div className="bg-white/5 rounded-lg p-2 flex-1">
                            <span className="text-[9px] font-bold text-text-muted uppercase block">
                              Nonce
                            </span>
                            <span className="text-xs font-mono text-secondary">
                              {block.nonce || 0}
                            </span>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2 flex-1">
                            <span className="text-[9px] font-bold text-text-muted uppercase block">
                              TXs
                            </span>
                            <span className="text-xs font-mono text-white">
                              {block.transactions?.length || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="p-4">
                    {block.status === "genesis" ? (
                      <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 text-center">
                        <span className="text-sm font-bold text-primary italic">
                          "The Genesis Block"
                        </span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {block.transactions?.map((t, idx) => (
                          <div
                            key={idx}
                            className="bg-surface/30 border border-white/5 rounded-lg p-2 hover:border-white/10 transition-colors"
                          >
                            <div className="flex items-center justify-between gap-4 mb-2">
                              <code className="text-[10px] text-text-muted font-mono truncate flex-1">
                                {t.hash}
                              </code>
                              <span className="text-[10px] font-bold text-secondary">
                                {t.amount} Coins
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-[9px] text-text-muted uppercase tracking-tighter">
                              <div className="flex items-center gap-1">
                                <span className="opacity-50">From:</span>
                                <span className="text-white font-mono">
                                  {renderTrimmedAddress(t.sender)}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="opacity-50">To:</span>
                                <span className="text-white font-mono">
                                  {renderTrimmedAddress(t.receiver)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-right align-top">
                    {block.status !== "genesis" && !block.id && (
                      <button
                        onClick={() => {
                          const allBlocks = [...blocks, ...pendingTransactions];
                          const previousBlock = allBlocks[index - 1];
                          const previousBlockHash = previousBlock
                            ? previousBlock.hash
                            : "0".repeat(64);

                          setSelectedBlock({
                            transactions: block.transactions || [],
                            previousBlockHash: previousBlockHash,
                          });
                        }}
                        className="bg-primary hover:bg-primary-hover text-white text-xs font-bold py-2.5 px-6 rounded-xl transition-all shadow-lg shadow-primary/20 active:scale-95 whitespace-nowrap"
                      >
                        Mine Block
                      </button>
                    )}
                    {block.hash && (
                      <div className="inline-flex items-center gap-2 text-green-500 text-[10px] font-bold bg-green-500/10 border border-green-500/20 px-3 py-1.5 rounded-full uppercase">
                        On Chain
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedBlock && (
        <div className="glass-card p-8 border-primary/30 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 blur-[100px] pointer-events-none" />
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center text-primary">
              <FiCpu size={20} />
            </div>
            Mining Visualizer
          </h2>
          <Visualizer
            transactions={selectedBlock.transactions}
            previousBlockHash={selectedBlock.previousBlockHash}
            save={saveBlock}
          />
        </div>
      )}
    </div>
  );
}

function Visualizer({
  transactions = [],
  previousBlockHash,
  initialNonce = 0,
  timestamp = dayjs().unix(),
  difficulty = 2,
  save,
}) {
  const [nonce, setNonce] = useState(initialNonce ?? 0);
  const [calculatedHash, setCalculatedHash] = useState("");
  const [foundHash, setFoundHash] = useState(false);
  const [roomId, setRoomId] = useState(localStorage.getItem("roomId") ?? "");

  async function mine(hash = calculateHash(transactions, nonce, timestamp, previousBlockHash)) {
    if (foundHash) return;

    const start = Date.now();

    let nonce = initialNonce;

    while (hash.slice(0, difficulty) !== "0".repeat(difficulty) && !foundHash) {
      const current = Date.now();

      hash = calculateHash(transactions, nonce, timestamp, previousBlockHash);

      setCalculatedHash(hash);

      const currentTime = Math.floor((current - start) / 1000);

      setNonce(++nonce);

      await new Promise((res) => setTimeout(res, 5));
    }

    setFoundHash(true);

    const end = Date.now();
    const time = (end - start) / 1000;
  }

  return (
    <div className="max-w-xl mx-auto">
      {transactions && transactions.length > 0 ? (
        <div className="bg-background/40 border border-white/5 rounded-3xl p-8 backdrop-blur-sm space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-text-muted uppercase tracking-widest ml-1">
                Current Nonce
              </label>
              <div className="bg-surface/50 border border-white/5 rounded-xl px-4 py-3 text-2xl font-mono text-secondary">
                {nonce}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-text-muted uppercase tracking-widest ml-1">
                Mining Status
              </label>
              <div
                className={clsx(
                  "border rounded-xl px-4 py-3 text-sm font-bold flex items-center justify-center gap-2 transition-all",
                  foundHash
                    ? "bg-green-500/10 border-green-500/20 text-green-500"
                    : "bg-primary/10 border-primary/20 text-primary animate-pulse"
                )}
              >
                {foundHash ? "SUCCESS" : "MINING..."}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-bold text-text-muted uppercase tracking-widest ml-1">
              Calculated Block Hash
            </label>
            <div className="bg-surface/50 border border-white/5 rounded-xl px-4 py-3 font-mono text-sm text-white break-all leading-relaxed">
              {calculatedHash || "Waiting to start..."}
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4">
            {!foundHash ? (
              <button
                onClick={() => mine()}
                className="w-full bg-primary hover:bg-primary-hover text-white font-bold py-4 rounded-2xl transition-all shadow-xl shadow-primary/30 text-lg active:scale-95"
              >
                Start Mining
              </button>
            ) : (
              <button
                onClick={() =>
                  save(calculatedHash, timestamp, previousBlockHash, transactions, nonce)
                }
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 rounded-2xl transition-all shadow-xl shadow-green-500/30 text-lg active:scale-95"
              >
                Save to Blockchain
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-text-muted">
          Select a block from the table above to start mining.
        </div>
      )}
    </div>
  );
}
